import api from '@nuskin/http-client';

import Sponsor from '../model/sponsor';
import SignupSchema from '../model/signup-schema';
import MockSignupClient from './mockSignupClient';
import SignupClientBase from '../interface/signupClientBase';
import * as BrowserStorageSessionStore from './browserStorageSessionStore.js';
import * as NoOpSessionStore from './noopSessionStore.js';
import queryString from 'query-string';

const SESSION_HEADER = 'X-Signup-Session';

/**
 * @typedef {import('@nuskin/http-client').HTTPRequest} HTTPRequest
 */

/**
 * @interface SessionStore
 */
/**
 * Load a session ID
 * @function
 * @name SessionStore#getSessionId
 * @returns {(string|null|Promise<string|null>)} stored session ID
 */
/**
 * Store the session ID
 * @function
 * @name SessionStore#setSessionId
 * @param {string} sessionId Session ID
 * @returns {(void|Promise)}
 */

/**
 * Signup client implementation which consumes the signup api through the AWS api gateway
 * @extends SignupClientBase
 * @property {string} clientId The client_id to use for service api calls
 * @property {SessionStore} sessionStore
 */
export default class SignupClient extends SignupClientBase {
    /**
     * @constructor
     * @param {object} config The configuration object which contains the stage and/or alias and url info
     * @param {string} config.stage The stage that the api is deployed in (e.g. "dev")
     * @param {string} [config.alias = null] The alias for the deployment (e.g. "feature/BSC-999")
     * @param {string} [config.url = null] The url to override, if supplied it ignores stage and alias
     * @param {string} [config.clientId] The clientId to use when talking to the signup api
     * @param {SessionStore|false} [config.sessionStore] How to store session IDs, or 'false' to disable sessions
     */
    constructor({
        stage,
        url = null,
        clientId,
        sessionStore
    }) {
        super();
        this.clientId = clientId;
        this.baseUrl = buildBaseUrl({
            stage,
            url
        });
        if (sessionStore === false) {
            this.sessionStore = NoOpSessionStore;
        } else {
            this.sessionStore = sessionStore || getDefaultSessionStore();
        }
    }

    /**
     * Returns a mock client instance.
     * @return {MockSignupClient} the mock instance
     */
    static mock() {
        return new MockSignupClient();
    }

    /**
     * @type {string | null}
     */
    get currentSessionId() {
        return this.sessionStore.getSessionId();
    }

    /**
     * @param {string | null} value
     */
    set currentSessionId(value) {
        this.sessionStore.setSessionId(value);
    }

    /**
     * Create a new signup session
     * @param {Object} p
     * @param {string} p.entrypoint
     * @param {string} p.market
     * @param {string} p.language
     * @param {string} p.signupType
     * @param {string} p.referrer
     * @return {Promise<string>} session ID
     */
    async createSession({
        entrypoint,
        market,
        language,
        signupType,
        referrer
    }) {
        const url = `${this.baseUrl}/v1/session`;
        const response = await api.post({
            url,
            headers: {
                client_id: this.clientId
            },
            body: {
                entrypoint,
                market,
                language,
                signupType,
                referrer
            }
        });
        const sessionId = response.body.data.signupSessionId;
        await this.sessionStore.setSessionId(sessionId);
        return sessionId;
    }

    /**
     * @callback WithSessionCallback
     * @param {Object<string, string>} headers
     * @return {Promise<HTTPRequest>}
     */

    /**
     * Wrap an API call in a session.
     *
     * @param {WithSessionCallback} callback callback
     * @return {Promise<HTTPRequest>} response
     */
    async _withSession(callback) {
        const sessionId = await this.sessionStore.getSessionId();
        try {
            const headers = sessionId ? {[SESSION_HEADER]: sessionId} : {};
            const response = await callback(headers);
            const newSession = findHeader(response.headers, SESSION_HEADER);
            if (newSession) {
                this.sessionStore.setSessionId(newSession);
            }
            return response;
        } catch (err) {
            if (err.response) {
                const newSession = findHeader(err.response.headers, SESSION_HEADER);
                if (newSession) {
                    this.sessionStore.setSessionId(newSession);
                }
            }
            throw err;
        }
    }

    /** @inheritdoc */
    async sponsorLookup(sponsorId, market, language, inviteId) {
        if (!sponsorId) throw new Error('Missing sponsor id');
        const sponsors = [];
        let url = `${this.baseUrl}/v1/sponsor/${sponsorId}`;
        const query = {};
        if (market && language) {
            query.locale = `${language}_${market.toUpperCase()}`;
        }
        if (inviteId) {
            query.inviteId = inviteId;
        }
        url += '?' + queryString.stringify(query);
        console.debug(url);
        try {
            const response = await this._withSession((headers) =>
                api.get({
                    url: url,
                    headers: {
                        'client_id': this.clientId,
                        ...headers
                    }
                }));
            const sponsorData = response.body.data;
            console.debug('Sponsor lookup ok for id: ', {id: response.body.data.id});
            sponsors.push(new Sponsor(sponsorData));
        } catch (error) {
            if (error.response) {
                if (error.response.status !== 404) {
                    throw error;
                } else {
                    console.warn('Warning: Not Found');
                }
            } else {
                throw error;
            }
        }
        return sponsors;
    }

    /** @inheritdoc */
    async getSponsorAccounts(sponsorId, market, language) {
        if (!sponsorId) throw new Error('Missing sponsor id');
        let url = `${this.baseUrl}/v1/sponsor-accounts/${sponsorId}`;
        if (market && language) {
            url += `?locale=${language}_${market.toUpperCase()}`;
        }
        console.debug(url);
        const response = await this._withSession((headers) => api.get({
            url: url,
            headers: {
                client_id: this.clientId,
                ...headers
            }
        }));
        const sponsorAccountsData = response.body.data;
        return sponsorAccountsData;
    }

    /** @inheritdoc */
    async getSignupSchema(market, signupType, language) {
        let url = `${this.baseUrl}/v1/account` +
            `/market/${market}/signup-type/${signupType}/language/${language}` +
            `/schema`;
        url += `?locale=${language}_${market.toUpperCase()}`;
        console.debug(url);
        try {
            const response = await this._withSession((headers) => api.get({
                url: url,
                headers: {
                    client_id: this.clientId,
                    ...headers
                }
            }));
            const bodyData = response.body.data;
            console.debug(response);
            const signupSchema = new SignupSchema(bodyData);
            return signupSchema;
        } catch (error) {
            throw error;
        }
    }

    /** @inheritdoc */
    async createAccount(market, signupType, language, modelObject, inviteId) {
        let url = `${this.baseUrl}/v1/account` +
            `/market/${market}/signup-type/${signupType}/language/${language}`;
        url += '?' + queryString.stringify({
            locale: language + '_' + market.toUpperCase(),
            inviteId
        }, {skipNull: true});
        console.debug(url);
        const response = await this._withSession((headers) => api.post({
            url: url,
            headers: {
                client_id: this.clientId,
                ...headers
            },
            body: {
                accountData: modelObject
            },
            timeout: 30000
        }));
        console.debug('createResponse:', {
            status: response.status,
            accountId: response.body.data.id
        });
        const createdAccount = response.body.data;
        return createdAccount;
    }

    /** @inheritdoc */
    async createGuestAccount(market, language, guestData) {
        let url = `${this.baseUrl}/v1/account` +
            `/market/${market}/language/${language}/guest`;
        url += `?locale=${language}_${market.toUpperCase()}`;
        console.debug(url);
        const response = await this._withSession((headers) => api.post({
            url: url,
            headers: {
                client_id: this.clientId,
                ...headers
            },
            body: guestData,
            timeout: 30000
        }));
        console.debug('guestCreateResponse:', {
            status: response.status,
            accountId: response.body.data.ID
        });
        const guestAccount = response.body.data;
        return guestAccount;
    }
}

/**
 * Builds the url which points to the signup api through the AWS gateway
 * @param {object} config The configuration object which contains the stage and optional alias and/or url info
 * @param {string} config.stage The stage that the api is deployed in (e.g. "dev")
 * @param {string} [config.alias = null] The alias for the deployment (e.g. "feature/BSC-999")
 * @param {string} [config.url = null] The url to override, if supplied it ignores stage and alias
 * @return {string} the generated base url
 */
function buildBaseUrl({
    stage,
    url
}) {
    let baseUrl;
    if (url) {
        baseUrl = url;
    } else if (!stage) {
        throw new Error('Missing stage parameter');
    } else {
        if (stage === 'local') {
            baseUrl = 'http://localhost:3000/signup';
        } else if (stage === 'dev') {
            baseUrl = 'https://devapi.cloud.nuskin.com/signup';
        } else if (stage === 'test') {
            baseUrl = 'https://testapi.cloud.nuskin.com/signup';
        } else if (stage === 'stage') {
            baseUrl = 'https://stageapi.cloud.nuskin.com/signup';
        } else if (stage === 'prod') {
            baseUrl = 'https://api.cloud.nuskin.com/signup';
        } else {
            throw new Error('Unknown stage: ' + stage);
        }
    }
    return baseUrl;
};

/* istanbul ignore next */
/**
 * @return {SessionStore} session store
 */
function getDefaultSessionStore() {
    // If we're in a browser...
    if (typeof window !== 'undefined' && window.sessionStorage) {
        return BrowserStorageSessionStore;
    }
    // For now, ignore node-based use cases
    return NoOpSessionStore;
}

/* istanbul ignore next */
/**
 * Find a header in an object of headers.
 *
 * @param {Object<string, string>} headers headers
 * @param {string} name header name
 * @return {string | null}
 */
function findHeader(headers, name) {
    if (!headers) {
        return headers;
    }
    const actualKey = Object.keys(headers).find((k) => k.toLowerCase() === name.toLowerCase());
    if (actualKey) {
        return headers[actualKey];
    } else {
        return null;
    }
}
